import React from 'react';
import { cn } from '@/utils';
import { VariantProps, cva } from 'class-variance-authority';

const typographyVariants = cva('', {
  variants: {
    variant: {
      h1: 'scroll-m-20 text-4xl font-extrabold tracking-tight lg:text-5xl',
      h2: 'scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight first:mt-0',
      h3: 'scroll-m-20 text-2xl font-semibold tracking-tight',
      h4: 'scroll-m-20 text-xl font-semibold tracking-tight',
      p: 'leading-7 [&:not(:first-child)]:mt-6',
      blockquote: 'mt-6 border-l-2 pl-6 italic',
      list: 'my-6 ml-6 list-disc [&>li]:mt-2',
      code: 'relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm font-semibold',
      lead: 'text-xl text-muted-foreground',
      large: 'text-lg font-semibold',
      small: 'text-sm font-medium leading-none',
      muted: 'text-sm text-muted-foreground',
      hero: 'text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight',
      section: 'text-3xl md:text-4xl font-bold tracking-tight',
      body: 'text-lg md:text-xl leading-relaxed',
      caption: 'text-sm text-muted-foreground leading-relaxed',
    },
  },
  defaultVariants: {
    variant: 'p',
  },
});

export interface TypographyProps
  extends React.HTMLAttributes<HTMLElement>,
    VariantProps<typeof typographyVariants> {
  as?: React.ElementType;
}

const Typography = React.forwardRef<HTMLElement, TypographyProps>(
  ({ className, variant, as, ...props }, ref) => {
    const Comp = as || getDefaultElement(variant);
    return React.createElement(
      Comp,
      {
        className: cn(typographyVariants({ variant, className })),
        ref,
        ...props,
      }
    );
  }
);

function getDefaultElement(variant: TypographyProps['variant']) {
  switch (variant) {
    case 'h1':
    case 'hero':
      return 'h1';
    case 'h2':
    case 'section':
      return 'h2';
    case 'h3':
      return 'h3';
    case 'h4':
      return 'h4';
    case 'blockquote':
      return 'blockquote';
    case 'list':
      return 'ul';
    case 'code':
      return 'code';
    case 'lead':
    case 'large':
    case 'small':
    case 'muted':
    case 'body':
    case 'caption':
    case 'p':
    default:
      return 'p';
  }
}

Typography.displayName = 'Typography';

export { Typography, typographyVariants };
export default Typography;